<template>
  <CCard v-if="temApresentacao" class="bloqueio">
    <CCardBody>
      <div class="bloqueio__cartao">
      <!--
        A chamada vem antes de qualquer menção a licença. Quem abre esta
        tela já quer o recurso; começar pelo cadeado responde "não pode"
        a uma pergunta que ele ainda não fez.
      -->
      <h3 class="bloqueio__titulo">{{ chamada }}</h3>

      <!--
        Sem repetir a descrição: ela está no "Como funciona", ao lado. Aqui
        vale a chamada, que é a pergunta que traz o leitor, e os destaques.
      -->
      <p v-if="conector.descricao_curta" class="bloqueio__resumo">
        {{ conector.descricao_curta }}
      </p>

      <!--
        O que muda no dia a dia de quem usa. É o que o conector declara
        como apresentação — diferente do tutorial, que fala a quem já
        contratou e vai configurar.
      -->
      <div v-if="destaques.length" class="bloqueio__destaques">
        <div v-for="(destaque, indice) in destaques" :key="indice" class="bloqueio__destaque">
          <strong>{{ destaque.titulo }}</strong>
          <span>{{ destaque.descricao }}</span>
        </div>
      </div>

      <!-- O que ele entrega, na mesma linguagem dos selos do catálogo. -->
      <div v-if="capacidades.length" class="bloqueio__capacidades">
        <span v-for="rotulo in capacidades" :key="rotulo" class="bloqueio__capacidade">
          {{ rotulo }}
        </span>
      </div>

      <!-- Ver o recurso funcionando vale mais que qualquer descrição. -->
      <div v-if="imagens.length" class="bloqueio__secao">
        <div class="bloqueio__secao-titulo">Como fica na tela</div>
        <div class="bloqueio__imagens">
          <!--
            Botão, e não figura clicável: a galeria abre com o teclado como
            abre com o mouse. O texto alternativo tem reserva porque a legenda
            é opcional no manifesto — sem ela, a imagem ficava sem descrição.
          -->
          <figure v-for="(imagem, indice) in imagens" :key="indice">
            <button type="button" class="imagem-botao" @click="abrir(indice)">
              <img
                :src="imagem.arquivo"
                :alt="imagem.legenda || ('Exemplo de ' + conector.nome)"
                loading="lazy"
              />
            </button>
            <figcaption v-if="imagem.legenda">{{ imagem.legenda }}</figcaption>
          </figure>
        </div>
      </div>

      <div v-if="!licenciado" class="bloqueio__acao">
        <a
          class="bloqueio__contato"
          :href="linkWhatsapp"
          target="_blank"
          rel="noopener noreferrer"
        >
          <fa-icon :icon="['fab', 'whatsapp']" /> {{ chamadaDoBotao }}
        </a>

        <p class="bloqueio__modulo">
          <fa-icon icon="lock" /> Faz parte do módulo <strong>{{ conector.licenca_nome }}</strong>,
          que esta instalação ainda não tem.
        </p>
      </div>

      </div>

      <!--
        Com mais de uma captura, ampliar uma e ter de fechar para ver a próxima
        faz o usuário repetir fechar-clicar-fechar seis vezes. A modal navega.
      -->
      <CModal
        :show="ampliada !== null"
        size="xl"
        :centered="true"
        :title="tituloDaGaleria"
        @update:show="fechar"
      >
        <div class="galeria">
          <button
            v-if="temGaleria"
            type="button"
            class="galeria__seta galeria__seta--anterior"
            title="Anterior"
            aria-label="Captura anterior"
            @click="anterior"
          >
            <fa-icon icon="chevron-left" />
          </button>

          <img
            v-if="imagemAmpliada"
            :src="imagemAmpliada.arquivo"
            :alt="imagemAmpliada.legenda"
            class="bloqueio__ampliada"
          />

          <button
            v-if="temGaleria"
            type="button"
            class="galeria__seta galeria__seta--proxima"
            title="Próxima"
            aria-label="Próxima captura"
            @click="proxima"
          >
            <fa-icon icon="chevron-right" />
          </button>
        </div>

        <!--
          As miniaturas dizem quantas faltam e deixam pular direto para uma
          delas — numa galeria de meia dúzia, procurar de seta em seta cansa.
        -->
        <div v-if="temGaleria" class="galeria__miniaturas">
          <button
            v-for="(imagem, indice) in imagens"
            :key="indice"
            type="button"
            class="galeria__miniatura"
            :class="{ 'galeria__miniatura--atual': indice === ampliada }"
            :title="imagem.legenda"
            @click="ampliada = indice"
          >
            <img :src="imagem.arquivo" :alt="imagem.legenda" loading="lazy" />
          </button>
        </div>

        <template #footer>
          <span v-if="temGaleria" class="galeria__posicao">
            {{ ampliada + 1 }} de {{ imagens.length }}
          </span>
          <CButton color="secondary" variant="outline" @click="fechar">Fechar</CButton>
        </template>
      </CModal>
    </CCardBody>
  </CCard>
</template>

<script>
import { linkWhatsappComercial } from '@/helpers/contatoComercial';

/*
 * O que o conector faz, mostrado na própria tela de configuração.
 *
 * Esconder o conector seria o pior dos mundos: o cliente nunca saberia que o
 * recurso existe, e o time comercial perderia a conversa que nasce justamente
 * de alguém procurando resolver um problema. Mostrar e deixar preencher seria
 * pior ainda — configuração que nunca funciona.
 *
 * Era um overlay: o formulário ficava desfocado atrás e um cartão flutuava por
 * cima. Virou seção da página porque as capturas valem para os dois públicos —
 * quem ainda não tem o recurso e quer entendê-lo, e quem já tem e está
 * configurando. Cobrir a tela servia só ao primeiro, e escondia justamente o
 * que o segundo veio fazer.
 *
 * Para quem não tem a licença, vem junto o caminho para contratar; para quem
 * tem, ficam só as capturas. Onde o recurso aparece depois de ligado é assunto
 * do tutorial, ao lado — repetir aqui dobrava a mesma lista na mesma tela.
 */
export default {
  name: 'ApresentacaoConector',
  props: {
    conector: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      // Posição da captura aberta, não a captura: é dela que sai a navegação.
      ampliada: null,
    };
  },
  /*
   * A seta do teclado é o gesto natural de quem está vendo uma galeria. O
   * ouvinte fica no documento porque a modal do CoreUI não recebe foco por
   * conta própria, e exigir um clique antes para "ativar" o teclado seria o
   * tipo de detalhe que ninguém descobre.
   */
  mounted() {
    document.addEventListener('keydown', this.aoTeclar);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.aoTeclar);
  },
  methods: {
    abrir(indice) {
      this.ampliada = indice;
    },

    fechar() {
      this.ampliada = null;
    },

    anterior() {
      this.ampliada = (this.ampliada - 1 + this.imagens.length) % this.imagens.length;
    },

    proxima() {
      this.ampliada = (this.ampliada + 1) % this.imagens.length;
    },

    aoTeclar(evento) {
      if (this.ampliada === null || !this.temGaleria) {
        return;
      }

      if (evento.key === 'ArrowLeft') {
        this.anterior();
      } else if (evento.key === 'ArrowRight') {
        this.proxima();
      }
    },
  },
  computed: {
    /*
     * Conector sem dependência de módulo vem liberado; a checagem contra
     * `false` evita tratar como bloqueado o conector que ainda não carregou.
     */
    licenciado() {
      return ! (this.conector && this.conector.licenca_liberada === false);
    },

    /**
     * Há o que mostrar?
     *
     * Para quem já tem a licença, só as capturas interessam — chamada e
     * destaques são material de venda, e quem está configurando já comprou.
     * Para quem não tem, vale tudo o que o conector souber dizer.
     */
    temApresentacao() {
      return this.licenciado
        ? this.imagens.length > 0
        : !! (this.destaques.length || this.imagens.length);
    },

    tutorial() {
      return (this.conector.manifesto && this.conector.manifesto.tutorial) || {};
    },

    /*
     * O bloco que o conector escreve para quem ainda não tem o recurso. É
     * outro texto que o do tutorial: ali se explica como configurar, aqui se
     * mostra o que muda quando existe.
     */
    apresentacao() {
      return (this.conector.manifesto && this.conector.manifesto.apresentacao) || {};
    },

    chamada() {
      return this.apresentacao.chamada || this.conector.nome;
    },

    chamadaDoBotao() {
      return this.apresentacao.botao || 'Falar com o comercial';
    },

    destaques() {
      return this.apresentacao.destaques || [];
    },

    /*
     * As da apresentação primeiro: elas mostram o recurso funcionando. As do
     * tutorial servem a quem vai configurar e, na falta das outras, ainda
     * valem mais do que nenhuma imagem.
     */
    imagens() {
      return this.apresentacao.imagens || this.tutorial.imagens || [];
    },

    capacidades() {
      return this.conector.capacidades_rotulos || [];
    },

    imagemAmpliada() {
      return this.ampliada === null ? null : this.imagens[this.ampliada];
    },

    /*
     * Com uma captura só não há para onde ir: setas, miniaturas e contador
     * somem, e a modal volta a ser o que era.
     */
    temGaleria() {
      return this.imagens.length > 1;
    },

    tituloDaGaleria() {
      return this.imagemAmpliada ? this.imagemAmpliada.legenda : '';
    },

    linkWhatsapp() {
      return linkWhatsappComercial(
        this.conector.licenca_nome || this.conector.nome,
        this.apresentacao.mensagem
      );
    },
  },
};
</script>

<style scoped>
.imagem-botao {
  background: none;
  border: 0;
  cursor: zoom-in;
  display: block;
  padding: 0;
  width: 100%;
}

.bloqueio__cartao {
  width: 100%;
}

.bloqueio__titulo {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1d2939;
}

.bloqueio__modulo {
  margin: 0;
  font-size: 12px;
  color: #667085;
}

.bloqueio__modulo strong {
  color: #3a3e81;
}

.bloqueio__resumo {
  margin: 0 0 16px;
  font-size: 13px;
  color: #475467;
  line-height: 1.6;
  white-space: pre-line;
  text-align: left;
}

.bloqueio__destaques {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  margin: 18px 0 4px;
  text-align: left;
}

.bloqueio__destaque {
  background: #f8f9fb;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
}

.bloqueio__destaque strong {
  color: #3a3e81;
  font-size: 13px;
}

.bloqueio__destaque span {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.45;
}

.bloqueio__acao {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
}

.bloqueio__capacidades {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-bottom: 20px;
}

.bloqueio__capacidade {
  padding: 3px 10px;
  border-radius: 12px;
  background: #eceaf3;
  color: #3a3e81;
  font-size: 11px;
  font-weight: 600;
}

.bloqueio__secao {
  margin-bottom: 20px;
  text-align: left;
}

.bloqueio__secao-titulo {
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e9ecef;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #667085;
}

/*
 * Miniaturas em grade, não uma captura por linha. Com meia dúzia de telas, a
 * coluna única fazia o cartão passar de quatro mil pixels e empurrava o botão
 * de contato para fora de qualquer rolagem razoável — quem rola seis telas
 * cheias já desistiu antes de chegar nele.
 */
.bloqueio__imagens {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.bloqueio__imagens figure {
  margin: 0;
  cursor: zoom-in;
}

/*
 * As capturas têm proporções diferentes — o softphone é alto, os painéis são
 * largos. A altura fixa com `cover` dá uma grade regular; o topo da imagem é o
 * que interessa, e o clique abre a captura inteira.
 */
.bloqueio__imagens img {
  display: block;
  width: 100%;
  height: 150px;
  object-fit: cover;
  object-position: top center;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #f8f9fb;
  transition: border-color 0.15s;
}

.bloqueio__imagens figure:hover img {
  border-color: #3a3e81;
}

.bloqueio__imagens figcaption {
  margin-top: 5px;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}

.bloqueio__contato {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border-radius: 8px;
  background: #25d366;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
}

.bloqueio__contato:hover {
  background: #1fb955;
  color: #fff;
  text-decoration: none;
}

.bloqueio__rodape {
  margin: 14px 0 0;
  font-size: 12px;
  color: #98a2b3;
}

.galeria {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  width: 100%;
}

/*
 * As setas ficam ao lado da imagem, não sobre ela: as capturas são telas
 * cheias de informação, e um botão por cima tapa justamente o canto que o
 * usuário ampliou para ler.
 */
.galeria__seta {
  align-items: center;
  background: #f2f2f8;
  border: 1px solid #dddce9;
  border-radius: 50%;
  color: #3a3e81;
  display: flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.galeria__seta:hover {
  background: #eceaf3;
  border-color: #3a3e81;
}

.galeria__miniaturas {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 14px;
}

.galeria__miniatura {
  background: none;
  border: 2px solid transparent;
  border-radius: 6px;
  line-height: 0;
  padding: 0;
}

.galeria__miniatura img {
  border: 1px solid #e4e7eb;
  border-radius: 4px;
  display: block;
  height: 44px;
  object-fit: cover;
  object-position: top center;
  width: 66px;
}

.galeria__miniatura--atual {
  border-color: #3a3e81;
}

.galeria__posicao {
  color: #667085;
  font-size: 12px;
  margin-right: auto;
}

.bloqueio__ampliada {
  display: block;
  /* Deixa espaço para as setas: com 100%, a imagem empurrava as duas para fora
     do corpo da modal, e elas apareciam soltas sobre o fundo escurecido. */
  max-width: calc(100% - 96px);
  max-height: 72vh;
  margin: 0 auto;
  border-radius: 6px;
}
</style>
